import React, { useMemo } from "react";
import { GetServerSidePropsContext, Page } from "next";
import { usePrematchTopWidgetConfig, useTranslation } from "@finbackoffice/site-core";
import { SiteContentScopes, PopupTags } from "@finbackoffice/enums";
import { TranslationScopes } from "@finbackoffice/enums";
import { HoursRange } from "@finbackoffice/websocket-client";
import { SportsbookGameItemTypeEnum } from "@finbackoffice/fe-core";
import Seo from "components/base/seo/Seo";
import ErrorBoundary from "components/base/error-boundary/ErrorBoundary";
import PrematchGameContainer from "components/pages/sport/prematch/game/PrematchGameContainer";
import { useSelectedPrematchGameId } from "hooks";
import PrematchTemplate from "components/layout/prematch/PrematchTemplate";
import { getSportsbookLayout } from "components/layout/sportsbook/SportsbookLayout";
import {
    clientBFFSingleton,
    getServerData,
    createGetServerSideProps,
    CommonSsrProps,
    getServerRuntimeConfig,
} from "services/ssr";
import { getCurrentLocale } from "utils/helpers";
import PrematchTopTournaments from "components/pages/sport/prematch/top/tournaments/PrematchTopTournaments";

export const getServerSideProps = createGetServerSideProps<CommonSsrProps>(
    async (context: GetServerSidePropsContext) => {
        const runtimeConfig = await getServerRuntimeConfig();
        const client = clientBFFSingleton.getInstance(context.req.cookies.jwt);
        const locale = getCurrentLocale(runtimeConfig, context.locale);

        const [{ translations, configs, profile, popupBanners, banners }] = await Promise.all([
            getServerData(runtimeConfig, context, client, {
                popupBanners: { scope: [PopupTags.Prematch], device: PopupTags.Desktop },
                translationScope: [TranslationScopes.PreMatch],
                banners: { scope: [SiteContentScopes.Prematch], strict: true },
            }),
        ]);

        return {
            props: {
                configs,
                runtimeConfig,
                jwt: context.req.cookies.jwt || "",
                profile: profile || null,
                locale,
                translations,
                banners: banners.items,
                popupBanners: popupBanners || [],
            },
        };
    },
);

const UpcomingRoute: Page = () => {
    const { t } = useTranslation();
    const selectedGameId = useSelectedPrematchGameId();
    const prematchTopWidgetConfig = usePrematchTopWidgetConfig();

    const hoursRange = useMemo(
        () => prematchTopWidgetConfig?.timeRange || HoursRange.ALL,
        [prematchTopWidgetConfig?.timeRange],
    );

    const renderLeftContent = useMemo(
        () => <PrematchTopTournaments hoursRange={hoursRange} />,
        [hoursRange],
    );

    const renderRightContent = useMemo(
        () => (
            <ErrorBoundary name={PrematchGameContainer.name}>
                <PrematchGameContainer gameId={selectedGameId} />
            </ErrorBoundary>
        ),
        [selectedGameId],
    );

    return (
        <>
            <Seo
                page={TranslationScopes.PreMatch}
                replace={{
                    title: { page_name: t("pageName_sport") },
                }}
            />
            <PrematchTemplate
                left={renderLeftContent}
                right={renderRightContent}
                variant={SportsbookGameItemTypeEnum.Flex}
            />
        </>
    );
};

UpcomingRoute.getLayout = getSportsbookLayout;

export default UpcomingRoute;
